// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import KeyboardEventListener from '@ta/events.keyboard-event-listener';
import { Track } from '@ta/common.tracking';
import FullScreenModal from '@ta/modal.fullscreen';
import { Closer, Global, Opener, AutoOpener } from '@ta/overlays.managers';
import { Backdrop, CloseIcon as CloseX } from '@ta/modal.core';
import { Target, Transition } from '@ta/overlays.pieces';
import { featureIsEnabled } from '@ta/common.features';
import { ACTIONS as SCROLL_FEED_TOOLTIP_ACTIONS } from '@ta/social.tooltip-state';
import sessionStorage from '@ta/common.sessionstorage';
import type { GeoPillSponsorDataProvider } from '@ta/brand.sponsored-geopill';
import GeoPillSearch from '@ta/brand.geopill-search';
import { useExplicitPreferencesModal } from '@ta/onboarding.explicit-preferences-entry';
import useExplicitPreferencesPreLoadInteraction from '@ta/onboarding.explicit-preferences-external-tracking';

import GeoPillRedCarpetContents from './GeoPillRedCarpetContents';
import styles from './geoPillCommon.less';

type ExternalProps = {|
  /** Trigger contents. targetRef should be set on the intended trigger as a ref. */
  children: (setTarget: () => void) => React.Node,

  /** Is it geo scoped */
  isGeoScoped?: boolean,

  /** is it nearby... */
  isNearby?: boolean,

  /** Promise for future geopill sponsor result or null if not enabled */
  sponsorDataProvider?: ?GeoPillSponsorDataProvider,
|};

type Props = {|
  ...ExternalProps,

  /** Is viewport desktop */
  isDesktop: boolean,

  /** Whether to show the flyout in fullscreen (used on mobile) */
  fullscreen: boolean,

  /** Imported action to show the scrolling tooltip */
  showScrollTooltip: () => void,

  /** Imported action to show the login bar */
  showLoginBar: () => void,
|};

const nextName: () => string = (() => {
  let i = 0;
  return () => {
    i += 1;
    return `redcarpet_${i}`;
  };
})();

const TRANSITION_TIMING = 450;
const FORCE_SCROLL_PROMPT = 'show_scoped_home_scroll_prompt';
const FORCE_LOGIN_BAR = 'show_scoped_home_login_bar';
const HAS_AUTOPOPPED = 'has_autopopped';

/**
 * "Red carpet" ~ "slide down" but more memorable.
 * This overlay widens then expands downwards in height. Because height is dependent on the contents that are passed into this overlay,
 * we also have a GeoPillRedCarpetContents component that handles the height transformation.
 */
const GeoPillRedCarpet = (props: Props) => {
  const [isDelayedAfterMount, setIsDelayedAfterMount] = React.useState(false);
  const [name] = React.useState(nextName());

  React.useEffect(() => {
    setTimeout(() => {
      setIsDelayedAfterMount(true);
    }, 500);
  }, [setIsDelayedAfterMount]);

  const hasAutoPopped = sessionStorage.has(HAS_AUTOPOPPED);

  const suppressAutoPop = () => {
    sessionStorage.set(HAS_AUTOPOPPED, true);
  };

  const shouldOpenOnLoad = () =>
    featureIsEnabled('social_homepage_autopop_geopill') &&
    !hasAutoPopped &&
    !props.isGeoScoped &&
    !props.isNearby &&
    props.isDesktop;

  const shouldPopScrollTooltipOnClose = () =>
    (featureIsEnabled('social_homepage_autopop_geopill') ||
      featureIsEnabled('social_homepage_new_headline')) &&
    !props.isGeoScoped &&
    !props.isNearby &&
    props.isDesktop;

  const onClose = () => {
    if (shouldPopScrollTooltipOnClose()) {
      // scroll tooltip via redux if it doesnt navigate
      props.showScrollTooltip();
      // pop login bar via redux if the page doesnt navigate
      props.showLoginBar();
    }
  };

  // When you select a result in the geo selector
  const onSelection = () => {
    if (shouldPopScrollTooltipOnClose()) {
      // Fire the scroll tooltip + login bar via session storage, as these require navigations
      // Pop the scrolling tooltip if it is going to navigate away from current page
      sessionStorage.set(FORCE_SCROLL_PROMPT, true);
      // Pop login bar if its going to navigate away
      sessionStorage.set(FORCE_LOGIN_BAR, true);
    }
  };

  const overlayContents = () => (
    <div className={styles.container} data-test-geopill-contents>
      <GeoPillSearch onSelection={onSelection} sponsorDataProvider={props.sponsorDataProvider} />
    </div>
  );

  const {
    open: openExplicitPreferences,
    isEnabled: explicitPreferencesEnabled,
  } = useExplicitPreferencesModal('GEOPILL');

  const { trackWouldOpenExplicitPreferences } = useExplicitPreferencesPreLoadInteraction(
    'GEOPILL',
    'GEOPILL_SELECTION',
  );

  return (
    <Opener name={name}>
      {(open, isOpen) => (
        <Closer>
          {close => {
            const closer = () => {
              close();
              onClose();
            };
            const opener = () => {
              const key = 'shown_geopill_explicit_preferences';
              if (!localStorage.getItem(key)) {
                localStorage.setItem(key, 'true');
                if (trackWouldOpenExplicitPreferences) {
                  trackWouldOpenExplicitPreferences();
                }
                if (explicitPreferencesEnabled) {
                  openExplicitPreferences();
                  return;
                }
              }

              open();
            };
            return (
              <React.Fragment>
                {isDelayedAfterMount && shouldOpenOnLoad() && (
                  <Track
                    mount={{
                      module: props.isGeoScoped || props.isNearby ? 'HomeScoped' : 'HomeUnscoped',
                      action: 'home_geopill_autopop',
                      nonInteraction: true,
                    }}
                  >
                    <AutoOpener
                      name={name}
                      markAsCheckedCallback={suppressAutoPop}
                      openLayerCheck={() => true}
                    />
                  </Track>
                )}
                <Target>
                  {(setTarget, target) => (
                    <React.Fragment>
                      <div onClick={isOpen ? close : opener}>{props.children(setTarget)}</div>

                      {props.fullscreen ? (
                        <Global name={name}>
                          <FullScreenModal disableAutoFocus>
                            <div className={styles.overlayContents}>
                              <div>
                                {overlayContents()}
                                <CloseX onClick={closer} testAttribute="geopill-red-carpet-close" />
                              </div>
                            </div>
                          </FullScreenModal>
                        </Global>
                      ) : (
                        <Global name={name} transitionDuration={TRANSITION_TIMING}>
                          <Transition.Consumer>
                            {transitionStatus => (
                              <Backdrop onClick={closer}>
                                <GeoPillRedCarpetContents
                                  target={target}
                                  transitionStatus={transitionStatus}
                                  close={closer}
                                  overlayContents={overlayContents()}
                                />
                                <KeyboardEventListener keyName="Escape" callback={closer} />
                              </Backdrop>
                            )}
                          </Transition.Consumer>
                        </Global>
                      )}
                    </React.Fragment>
                  )}
                </Target>
              </React.Fragment>
            );
          }}
        </Closer>
      )}
    </Opener>
  );
};

export default connect<Props, ExternalProps, _, _, _, _>(
  (state, props: ExternalProps) => ({
    ...props,
    fullscreen: state.meta.device.viewportCategory === 'MOBILE',
    isDesktop: state.meta.device.viewportCategory === 'DESKTOP',
  }),
  {
    showScrollTooltip: SCROLL_FEED_TOOLTIP_ACTIONS.showScrollTooltip,
    showLoginBar: SCROLL_FEED_TOOLTIP_ACTIONS.showLoginBar,
  },
)(GeoPillRedCarpet);
